/**
 * 食材資料庫
 *
 * 欄位:
 * - id          唯一識別,正整數(臨時食材使用負整數,不會與此衝突)
 * - name        繁體中文短名
 * - type        顯示分類 'protein' | 'veg' | 'fruit' | 'starch' | 'snack'
 * - recipeRole  配餐角色  'protein' | 'plant' | 'excluded'
 *               解耦「使用者看到的分類」與「演算法分配熱量的角色」
 * - safety      'safe' | 'caution' | 'toxic'
 * - calories/protein/fat/ca/p — 每 100g 的營養素
 *   (kcal / g / g / mg / mg)
 *
 * 數值來源:USDA FoodData Central(SR Legacy)、衛福部食品藥物管理署
 * 食品營養成分資料庫,皆取「生 / 未調味」基準。配餐目的取整數
 * (±5% 誤差不影響鈣磷比與熱量分配的可信度)。
 *
 * 在 PetMD / WSAVA / AAFCO 等公開來源被列為犬隻不安全的食物
 * (巧克力、葡萄/葡萄乾、洋蔥、大蒜、夏威夷豆、酪梨種子等)
 * 一律 safety: 'toxic' + recipeRole: 'excluded'。
 */

export const FOOD_DATABASE = [
  // === 蛋白質:肉類 ===
  {
    id: 1,
    name: '雞胸肉',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'safe',
    calories: 105,
    protein: 23,
    fat: 1,
    ca: 5,
    p: 200,
    desc: '低脂優質蛋白,鮮食基底。',
    warning: null,
  },
  {
    id: 2,
    name: '雞腿肉',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'safe',
    calories: 150,
    protein: 18,
    fat: 8,
    ca: 8,
    p: 180,
    desc: '適口性佳,脂肪含量稍高。',
    warning: null,
  },
  {
    id: 11,
    name: '牛腱肉',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'safe',
    calories: 130,
    protein: 21,
    fat: 4,
    ca: 6,
    p: 200,
    desc: '紅肉鐵質豐富,瘦切煮熟切丁。',
    warning: null,
  },
  {
    id: 12,
    name: '豬里肌',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'safe',
    calories: 130,
    protein: 22,
    fat: 4,
    ca: 5,
    p: 210,
    desc: '低脂白肉替代,確實煮熟避免寄生蟲。',
    warning: null,
  },
  {
    id: 13,
    name: '羊肉(瘦)',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'safe',
    calories: 130,
    protein: 22,
    fat: 5,
    ca: 6,
    p: 200,
    desc: '低致敏,適合食物過敏的犬隻。',
    warning: null,
  },
  {
    id: 14,
    name: '鴨胸肉',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'safe',
    calories: 135,
    protein: 19,
    fat: 6,
    ca: 8,
    p: 200,
    desc: '去皮使用,風味重。',
    warning: null,
  },

  // === 蛋白質:內臟 ===
  {
    id: 15,
    name: '雞肝',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'caution',
    calories: 119,
    protein: 19,
    fat: 5,
    ca: 8,
    p: 310,
    desc: '維他命 A 極高,鈣磷比偏低。',
    warning: '建議不超過總食量 5%,過量會維他命 A 中毒',
  },
  {
    id: 16,
    name: '豬肝',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'caution',
    calories: 121,
    protein: 21,
    fat: 4,
    ca: 6,
    p: 285,
    desc: '鐵與葉酸豐富。',
    warning: '建議不超過總食量 5%',
  },

  // === 蛋白質:海鮮 ===
  {
    id: 6,
    name: '鮭魚',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'safe',
    calories: 208,
    protein: 20,
    fat: 13,
    ca: 9,
    p: 200,
    desc: '富含 Omega-3,務必去刺煮熟。',
    warning: null,
  },
  {
    id: 17,
    name: '鯖魚',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'safe',
    calories: 205,
    protein: 19,
    fat: 14,
    ca: 12,
    p: 230,
    desc: 'Omega-3 比鮭魚還高,選新鮮去刺煮熟。',
    warning: null,
  },
  {
    id: 18,
    name: '鱈魚',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'safe',
    calories: 84,
    protein: 18,
    fat: 0.7,
    ca: 16,
    p: 200,
    desc: '低脂白肉魚,腸胃敏感犬適用。',
    warning: null,
  },
  {
    id: 19,
    name: '吻仔魚',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'caution',
    calories: 80,
    protein: 15,
    fat: 1,
    ca: 200,
    p: 230,
    desc: '天然鈣質補充,連骨吃。',
    warning: '注意鈉含量,少量使用',
  },

  // === 蛋白質:蛋與乳製品 ===
  {
    id: 20,
    name: '全蛋(熟)',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'safe',
    calories: 155,
    protein: 13,
    fat: 11,
    ca: 50,
    p: 172,
    desc: '完整氨基酸來源,務必煮熟避免生物素缺乏。',
    warning: null,
  },
  {
    id: 21,
    name: '蛋黃',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'safe',
    calories: 317,
    protein: 16,
    fat: 27,
    ca: 129,
    p: 390,
    desc: '卵磷脂與脂溶性維生素豐富,熱量高。',
    warning: null,
  },
  {
    id: 22,
    name: '無糖優格',
    type: 'protein',
    recipeRole: 'protein',
    safety: 'caution',
    calories: 60,
    protein: 4,
    fat: 3,
    ca: 120,
    p: 95,
    desc: '益生菌來源,選無糖無代糖款。',
    warning: '乳糖不耐犬會拉肚子,首次少量嘗試',
  },

  // === 蔬菜 ===
  {
    id: 3,
    name: '南瓜',
    type: 'veg',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 26,
    protein: 1,
    fat: 0.1,
    ca: 21,
    p: 44,
    desc: '富含纖維,對腸胃好。需煮熟。',
    warning: null,
  },
  {
    id: 4,
    name: '紅蘿蔔',
    type: 'veg',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 41,
    protein: 0.9,
    fat: 0.2,
    ca: 33,
    p: 35,
    desc: '富含維生素A,油炒吸收更好。',
    warning: null,
  },
  {
    id: 7,
    name: '花椰菜',
    type: 'veg',
    recipeRole: 'plant',
    safety: 'caution',
    calories: 34,
    protein: 2.8,
    fat: 0.4,
    ca: 47,
    p: 66,
    desc: '十字花科,甲狀腺問題犬少吃。',
    warning: '建議每日不超過總食量 10%,需煮熟',
  },
  {
    id: 23,
    name: '高麗菜',
    type: 'veg',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 25,
    protein: 1.3,
    fat: 0.1,
    ca: 40,
    p: 26,
    desc: '便宜耐放好消化,煮過更好吸收。',
    warning: null,
  },
  {
    id: 24,
    name: '青江菜',
    type: 'veg',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 13,
    protein: 1.5,
    fat: 0.2,
    ca: 105,
    p: 37,
    desc: '高鈣低熱量,綠葉首選。',
    warning: null,
  },
  {
    id: 25,
    name: '小白菜',
    type: 'veg',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 13,
    protein: 1.5,
    fat: 0.2,
    ca: 90,
    p: 30,
    desc: '高水分高鈣,適合切碎拌飯。',
    warning: null,
  },
  {
    id: 26,
    name: '菠菜',
    type: 'veg',
    recipeRole: 'plant',
    safety: 'caution',
    calories: 23,
    protein: 2.9,
    fat: 0.4,
    ca: 99,
    p: 49,
    desc: '營養密度高,但含草酸。',
    warning: '泌尿系統結石或腎臟病犬避免;一般犬限量',
  },
  {
    id: 27,
    name: '番茄(熟)',
    type: 'veg',
    recipeRole: 'plant',
    safety: 'caution',
    calories: 18,
    protein: 0.9,
    fat: 0.2,
    ca: 10,
    p: 24,
    desc: '只取熟紅番茄果肉,去蒂去莖。',
    warning: '青番茄、莖葉含茄鹼,絕對不可餵',
  },

  // === 水果 ===
  {
    id: 28,
    name: '蘋果(去籽)',
    type: 'fruit',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 52,
    protein: 0.3,
    fat: 0.2,
    ca: 6,
    p: 11,
    desc: '富含果膠與維他命 C,務必去籽去核。',
    warning: null,
  },
  {
    id: 29,
    name: '藍莓',
    type: 'fruit',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 57,
    protein: 0.7,
    fat: 0.3,
    ca: 6,
    p: 12,
    desc: '抗氧化物豐富的零食水果。',
    warning: null,
  },
  {
    id: 30,
    name: '香蕉',
    type: 'fruit',
    recipeRole: 'plant',
    safety: 'caution',
    calories: 89,
    protein: 1.1,
    fat: 0.3,
    ca: 5,
    p: 22,
    desc: '高鉀好消化,適合運動後補充。',
    warning: '高糖,糖尿病犬與肥胖犬少量;每日不超過 1 小段',
  },
  {
    id: 31,
    name: '木瓜',
    type: 'fruit',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 43,
    protein: 0.5,
    fat: 0.3,
    ca: 20,
    p: 10,
    desc: '木瓜酵素助消化,去皮去籽。',
    warning: null,
  },

  // === 澱粉 / 主食 ===
  {
    id: 5,
    name: '地瓜',
    type: 'starch',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 86,
    protein: 1.6,
    fat: 0.1,
    ca: 30,
    p: 47,
    desc: '優質碳水,但熱量較高需控制。',
    warning: null,
  },
  {
    id: 32,
    name: '馬鈴薯(熟)',
    type: 'starch',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 87,
    protein: 1.9,
    fat: 0.1,
    ca: 12,
    p: 57,
    desc: '需完全煮熟,避免發芽變綠的部分。',
    warning: null,
  },
  {
    id: 33,
    name: '糙米飯',
    type: 'starch',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 111,
    protein: 2.6,
    fat: 0.9,
    ca: 10,
    p: 83,
    desc: '比白米保留更多 B 群與纖維。',
    warning: null,
  },
  {
    id: 34,
    name: '燕麥(熟)',
    type: 'starch',
    recipeRole: 'plant',
    safety: 'safe',
    calories: 71,
    protein: 2.5,
    fat: 1.5,
    ca: 9,
    p: 77,
    desc: '低 GI 碳水,適合長毛犬皮毛。',
    warning: null,
  },

  // === 禁忌 (toxic) ===
  {
    id: 8,
    name: '巧克力',
    type: 'snack',
    recipeRole: 'excluded',
    safety: 'toxic',
    calories: 546,
    protein: 5,
    fat: 30,
    ca: 0,
    p: 0,
    desc: '絕對禁止!含可可鹼。',
    warning: '致死風險:心臟衰竭',
  },
  {
    id: 9,
    name: '葡萄',
    type: 'fruit',
    recipeRole: 'excluded',
    safety: 'toxic',
    calories: 67,
    protein: 0.6,
    fat: 0.4,
    ca: 0,
    p: 0,
    desc: '絕對禁止!含未知毒素,葡萄乾同樣禁止。',
    warning: '致死風險:急性腎衰竭',
  },
  {
    id: 10,
    name: '洋蔥',
    type: 'veg',
    recipeRole: 'excluded',
    safety: 'toxic',
    calories: 40,
    protein: 1.1,
    fat: 0.1,
    ca: 0,
    p: 0,
    desc: '破壞紅血球。',
    warning: '致死風險:溶血性貧血',
  },
  {
    id: 35,
    name: '大蒜',
    type: 'veg',
    recipeRole: 'excluded',
    safety: 'toxic',
    calories: 149,
    protein: 6.4,
    fat: 0.5,
    ca: 0,
    p: 0,
    desc: '與洋蔥同屬蔥科,毒性更強。',
    warning: '致死風險:溶血性貧血',
  },
  {
    id: 36,
    name: '夏威夷豆',
    type: 'snack',
    recipeRole: 'excluded',
    safety: 'toxic',
    calories: 718,
    protein: 7.9,
    fat: 76,
    ca: 0,
    p: 0,
    desc: '澳洲胡桃,即使少量也會中毒。',
    warning: '症狀:虛弱、嘔吐、後肢無力,送醫',
  },
  {
    id: 37,
    name: '酪梨',
    type: 'fruit',
    recipeRole: 'excluded',
    safety: 'toxic',
    calories: 160,
    protein: 2,
    fat: 15,
    ca: 0,
    p: 0,
    desc: '果肉含 persin,皮與籽更毒。',
    warning: '致死風險:嘔吐、心肌損傷',
  },
];

/** O(1) 查詢:藉由 id 取食材 */
const FOOD_BY_ID = new Map(FOOD_DATABASE.map((f) => [f.id, f]));
export const getFoodById = (id) => FOOD_BY_ID.get(id) ?? null;

/**
 * 食材類型標籤(顯示用)
 * 順序也是 picker 與搜尋頁分組的呈現順序。
 */
export const FOOD_TYPES = {
  protein: '蛋白質',
  veg: '蔬菜',
  fruit: '水果',
  starch: '澱粉/主食',
  snack: '零食',
};

/**
 * 安全等級標籤
 */
export const SAFETY_LEVELS = {
  safe: { label: '安全', tone: 'green' },
  caution: { label: '注意', tone: 'yellow' },
  toxic: { label: '禁止', tone: 'red' },
};
